import { useEffect, useState, useRef, lazy, Suspense } from "react";
import { Routes, Route, useLocation, useNavigate, Navigate, useParams } from "react-router-dom";
import { isReservedWorkoutsDynamicId } from "./utils/workoutsRouteReserved";
import { findPausedWorkoutLive } from "./utils/workoutLiveSession";
import useAppStore from "./stores/useAppStore";
import useSessionStore from "./stores/useSessionStore";
import { T } from "./styles/GlobalStyles";
import { formatSessionDuration } from "./utils/format";
import { generateSessionForClient } from "./services/generateSession";
import { initAnalytics, identify, page, track, EVENTS } from "./services/analytics";
import Spin from "./components/common/Spin";
import Toast from "./components/common/Toast";
import Sidebar from "./components/layout/Sidebar";
import SessionTopBar from "./components/layout/SessionTopBar";
import LiveBanner from "./components/live/LiveBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import { useDeviceContext } from "./hooks/useDeviceContext";
import { useAutoRegen } from "./hooks/useSettingsRegen";
import ModalWrapper from "./components/shared/ModalWrapper";
import { useAuth } from "./contexts/AuthContext";
import { isSupabaseConfigured } from "./services/supabase";

import SplashScreen from "./components/SplashScreen";

const DashboardView = lazy(() => import("./views/DashboardView"));
const ClientsView = lazy(() => import("./views/ClientsView"));
const ClientProfileView = lazy(() => import("./views/ClientProfileView"));
const SessionView = lazy(() => import("./views/SessionView"));
const CalendarView = lazy(() => import("./views/CalendarView"));
const BillingView = lazy(() => import("./views/BillingView"));
const InsightsView = lazy(() => import("./views/InsightsView"));
const SettingsView = lazy(() => import("./views/SettingsView"));
const WorkoutsListView = lazy(() => import("./views/workouts/WorkoutsListView"));
const WorkoutBuilderView = lazy(() => import("./views/workouts/WorkoutBuilderView"));
const WorkoutDetailView = lazy(() => import("./views/workouts/WorkoutDetailView"));
const WorkoutLiveView = lazy(() => import("./views/workouts/WorkoutLiveView"));
const WorkoutNotesView = lazy(() => import("./views/workouts/WorkoutNotesView"));
const SessionCreationView = lazy(() => import("./views/workouts/SessionCreationView"));
const ProgramsPlaceholderView = lazy(() => import("./views/workouts/ProgramsPlaceholderView"));
const ExerciseLibraryView = lazy(() => import("./views/workouts/ExerciseLibraryView"));

function LegacyWorkoutEditRedirect() {
  const { id } = useParams();
  if (!id || isReservedWorkoutsDynamicId(id)) {
    return <Navigate to="/workouts/sessions" replace />;
  }
  return <Navigate to={`/workouts/sessions/${id}/edit`} replace />;
}

function LazyWrap({ children }) {
  return (
    <Suspense
      fallback={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", width: "100%" }}>
          <Spin />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

export default function AppShell() {
  const location = useLocation();
  const navigate = useNavigate();
  const isWorkouts = location.pathname.startsWith("/workouts");
  const isLiveMode = /\/workouts\/[^/]+\/(live|notes)$/.test(location.pathname);
  const onLiveRoute = /^\/workouts\/[^/]+\/live$/.test(location.pathname);
  const pausedSession = findPausedWorkoutLive();
  const { user, signOut, isAdmin } = useAuth();
  const booting = useAppStore((s) => s.booting);
  const loggedIn = useAppStore((s) => s.loggedIn);
  const onboarded = useAppStore((s) => s.onboarded);
  const splashSeen = useAppStore((s) => s.splashSeen);
  const setSplashSeen = useAppStore((s) => s.setSplashSeen);
  const theme = useAppStore((s) => s.theme);
  const brandName = useAppStore((s) => s.brandName);
  const logoUrl = useAppStore((s) => s.logoUrl);
  const view = useAppStore((s) => s.view);
  const setView = useAppStore((s) => s.setView);
  const setViewRaw = useAppStore((s) => s.setViewRaw);
  const hydrate = useAppStore((s) => s.hydrate);
  const flushPersist = useAppStore((s) => s.flushPersist);
  const selectedClient = useAppStore((s) => s.selectedClient);
  const settingsDirty = useAppStore((s) => s.settingsDirty);
  const demoMode = useAppStore((s) => s.demoMode);
  const toggleDemoMode = useAppStore((s) => s.toggleDemoMode);

  const [pendingNav, setPendingNav] = useState(null);

  const activeSession = useSessionStore((s) => s.activeSession);
  const sessionNow = useSessionStore((s) => s.sessionNow);
  const tick = useSessionStore((s) => s.tick);

  const analyticsReady = useRef(false);

  useEffect(() => {
    if (!isSupabaseConfigured()) {
      void hydrate();
    }
    initAnalytics();
  }, [hydrate]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") void flushPersist();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [flushPersist]);

  useEffect(() => {
    if (booting || !loggedIn) return;
    if (isWorkouts) {
      track(EVENTS.PAGE_VIEWED, { page: "workouts", path: location.pathname, liveMode: isLiveMode });
      return;
    }
    track(EVENTS.PAGE_VIEWED, { page: view });
  }, [booting, loggedIn, view, isWorkouts, isLiveMode, location.pathname]);

  useEffect(() => {
    if (booting || !loggedIn || analyticsReady.current) return;
    analyticsReady.current = true;
    const state = useAppStore.getState();
    identify(user?.email || state.coachProfile?.email || state.coachEmail || "coach", {
      name: state.coachProfile?.name,
      clientCount: state.clients?.length,
      theme,
      demoMode,
    });
    track(EVENTS.APP_LOADED, { clientCount: state.clients?.length });
  }, [booting, loggedIn, theme, demoMode, user?.email]);

  const lastCronDate = useRef(null);

  const runAutoGen = useRef(async () => {
    const state = useAppStore.getState();
    if (!state.loggedIn || !state.onboarded) return;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eligible = state.clients.filter(
      (c) =>
        c.needsAiGen &&
        c.status !== "archived" &&
        c.lastSessionEndedAt &&
        new Date(c.lastSessionEndedAt) < today
    );
    if (!eligible.length) return;
    for (const cl of eligible) {
      try {
        const fresh = useAppStore.getState();
        const client = fresh.clients.find((c) => c.id === cl.id);
        if (!client || !client.needsAiGen) continue;
        const session = await generateSessionForClient(client, fresh.clients, fresh.equipment, fresh.coachProfile);
        if (session) {
          track(EVENTS.AI_SESSION_GENERATED, {
            trigger: "cron",
            clientId: cl.id,
            hoursSinceSession: Math.round((Date.now() - cl.lastSessionEndedAt) / 3600000),
          });
          // @deprecated Workouts 1b — chemin legacy JSON (nextAiSession/needsAiGen/aiSessionStale).
          // À migrer vers persistAiSessionForClient (vraie instance jouable) — Workouts 1c.
          fresh.setClients((p) =>
            p.map((c) =>
              c.id === cl.id ? { ...c, nextAiSession: session, needsAiGen: false, aiSessionStale: false } : c
            )
          );
        }
      } catch {
        /* silent */
      }
    }
  }).current;

  useEffect(() => {
    if (booting || !loggedIn || !onboarded) return;

    const checkAndRun = () => {
      const todayStr = new Date().toDateString();
      if (lastCronDate.current === todayStr) return;
      lastCronDate.current = todayStr;
      track(EVENTS.APP_RESUMED, { hour: new Date().getHours() });
      runAutoGen();
    };

    checkAndRun();

    const onVisibility = () => {
      if (!document.hidden) checkAndRun();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [booting, loggedIn, onboarded]);

  useAutoRegen();

  useEffect(() => {
    if (!activeSession) return;
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeSession, tick]);

  const guardedSetView = (v) => {
    if (view === "settings" && settingsDirty) {
      setPendingNav({ type: "setView", target: v });
      return;
    }
    page(v);
    setView(v);
  };
  const guardedSetViewRaw = (v) => {
    if (view === "settings" && settingsDirty && v !== "settings") {
      setPendingNav({ type: "setViewRaw", target: v });
      return;
    }
    page(v);
    setViewRaw(v);
  };

  const navView = isWorkouts
    ? "workouts"
    : ["dashboard", "clients", "calendar", "insights", "billing", "settings"].includes(view)
      ? view
      : "clients";
  const fmtDuration = activeSession ? formatSessionDuration(activeSession.startedAt, sessionNow) : "";
  useDeviceContext();

  if (booting) {
    return <div style={{ height: "100vh", background: T.bg }} />;
  }

  if (!splashSeen) {
    return <SplashScreen onDone={() => setSplashSeen(true)} />;
  }

  const renderView = () => {
    if (view === "session" && activeSession) return <SessionView />;
    if (view === "dashboard") return <DashboardView />;
    if (view === "clients") return <ClientsView />;
    if (view === "client-profile" && selectedClient) return <ClientProfileView />;
    if (view === "calendar") return <CalendarView />;
    if (view === "insights") return <InsightsView />;
    if (view === "billing") return <BillingView />;
    if (view === "settings") return <SettingsView />;
    if (activeSession) return <SessionView />;
    return null;
  };

  return (
    <>
      <a
        href="#main-content"
        className="skip-link"
        style={{
          position: "absolute",
          left: -9999,
          top: "auto",
          width: 1,
          height: 1,
          overflow: "hidden",
          zIndex: 10000,
          padding: "12px 24px",
          background: T.accent,
          color: T.bg,
          fontWeight: 700,
          fontSize: 14,
          borderRadius: "0 0 8px 8px",
          textDecoration: "none",
        }}
      >
        Aller au contenu principal
      </a>
      <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden" }}>
        {!isLiveMode && (
        <Sidebar
          view={navView}
          setView={guardedSetView}
          onNavItem={(id) => {
            if (id === "workouts") {
              navigate("/workouts/sessions");
              return;
            }
            if (isWorkouts) navigate("/");
            guardedSetView(id);
          }}
          onLogoClick={() => {
            if (isWorkouts) navigate("/");
            guardedSetView("dashboard");
          }}
          onSettings={() => {
            if (isWorkouts) navigate("/");
            guardedSetViewRaw("settings");
          }}
          brandName={brandName}
          logoUrl={logoUrl}
          demoMode={demoMode}
          onToggleDemo={() => {
            toggleDemoMode();
            const next = !demoMode;
            track(EVENTS.DEMO_MODE_TOGGLED, { enabled: next });
            useAppStore.getState().showToast(next ? "Mode démo activé" : "Mode vide activé");
          }}
          onSignOut={isSupabaseConfigured() ? signOut : undefined}
          onResetDemo={() => {
            useAppStore.getState().resetDemoData();
            track(EVENTS.DEMO_MODE_TOGGLED, { reset: true });
          }}
          adminHref={isSupabaseConfigured() && isAdmin ? "/admin" : undefined}
        />
        )}
        <main
          id="main-content"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            width: isLiveMode ? "100%" : undefined,
            maxWidth: isLiveMode ? "100vw" : undefined,
          }}
        >
          {pausedSession && !onLiveRoute && (
            <LiveBanner
              workoutTitle={pausedSession.snapshot?.workoutTitle}
              clientName={pausedSession.snapshot?.clientName}
              onResume={() => navigate(`/workouts/${pausedSession.workoutId}/live`)}
            />
          )}
          {activeSession && view !== "session" && !isLiveMode && (
            <SessionTopBar
              activeSession={activeSession}
              formatDuration={fmtDuration}
              onGoToSession={() => guardedSetViewRaw("session")}
            />
          )}
          <div style={{ flex: 1, minHeight: 0, overflow: isLiveMode ? "auto" : "hidden" }}>
            <ErrorBoundary>
              <LazyWrap>
                {isWorkouts ? (
                  <Routes>
                    <Route path="/workouts" element={<Navigate to="/workouts/exercises" replace />} />
                    <Route path="/workouts/new" element={<Navigate to="/workouts/sessions/new" replace />} />
                    <Route path="/workouts/sessions/new" element={<SessionCreationView />} />
                    <Route path="/workouts/sessions/:id/edit" element={<WorkoutBuilderView />} />
                    <Route path="/workouts/sessions" element={<WorkoutsListView />} />
                    <Route path="/workouts/exercises" element={<ExerciseLibraryView />} />
                    <Route path="/workouts/programs" element={<ProgramsPlaceholderView />} />
                    <Route path="/workouts/:id/live" element={<WorkoutLiveView />} />
                    <Route path="/workouts/:id/notes" element={<WorkoutNotesView />} />
                    <Route path="/workouts/:id/edit" element={<LegacyWorkoutEditRedirect />} />
                    <Route path="/workouts/:id" element={<WorkoutDetailView />} />
                  </Routes>
                ) : (
                  renderView()
                )}
              </LazyWrap>
            </ErrorBoundary>
          </div>
        </main>
      </div>

      <Toast />

      <ModalWrapper
        open={!!pendingNav}
        onClose={() => setPendingNav(null)}
        titleId="pending-nav-title"
        size="sm"
        style={{ textAlign: "center" }}
      >
        <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
        <div id="pending-nav-title" className="condensed" style={{ fontSize: 20, fontWeight: 700, marginBottom: 6 }}>
          Modifications non sauvegardées
        </div>
        <div style={{ fontSize: 13, color: T.muted, marginBottom: 22, lineHeight: 1.5 }}>
          Tu as des modifications en cours dans les paramètres. Que veux-tu faire ?
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={() => {
              useAppStore.getState().setSettingsDirty(false);
              if (pendingNav.type === "setView") setView(pendingNav.target);
              else setViewRaw(pendingNav.target);
              setPendingNav(null);
            }}
            style={{ flex: 1, padding: "11px", borderRadius: 11, border: `1px solid ${T.border}`, color: T.muted, fontSize: 13 }}
          >
            Quitter sans sauvegarder
          </button>
          <button
            onClick={() => setPendingNav(null)}
            style={{ flex: 1, padding: "11px", borderRadius: 999, background: T.accent, color: T.bg, fontWeight: 700, fontSize: 13 }}
          >
            Rester
          </button>
        </div>
      </ModalWrapper>
    </>
  );
}
